import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TGChibi from './TGChibi';
import {
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  GlobeAltIcon,
  DocumentMagnifyingGlassIcon,
  ChartBarIcon,
  CogIcon,
  ShieldCheckIcon,
  TagIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle hash scrolling
  React.useEffect(() => {
    if (location.hash) {
      if (location.hash === '#top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const element = document.getElementById(location.hash.replace('#', ''));
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
    } else {
      // If no hash, ensure we scroll to top (important for page transitions like /analysis)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  const dashboardNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Domains', href: '/domains', icon: GlobeAltIcon },
    { name: 'Scans', href: '/scans', icon: DocumentMagnifyingGlassIcon },
    { name: 'Reports', href: '/reports', icon: ChartBarIcon },
    { name: 'Whitelist', href: '/whitelist', icon: TagIcon },
    { name: 'SOP', href: '/sop', icon: ClipboardDocumentListIcon },
    { name: 'Settings', href: '/settings', icon: CogIcon },
    { name: 'Contact', href: '/contact', icon: EnvelopeIcon },
  ];

  const landingNavigation = [
    { name: 'How It Works', href: '/#how-it-works', icon: MagnifyingGlassIcon },
    { name: 'Why TypoGuard?', href: '/#why-typoguard', icon: ShieldCheckIcon },
    { name: 'See Inside', href: '/#demo', icon: EyeIcon },
    { name: 'Plans', href: '/#pricing', icon: TagIcon },
    { name: 'FAQ', href: '/#faq', icon: ClipboardDocumentListIcon },
    { name: 'Blog', href: '/blog/typosquatting-detection-guide#top', icon: DocumentMagnifyingGlassIcon },
    { name: 'Analysis', href: '/analysis#top', icon: ChartBarIcon },
  ];

  const adminNavigation = [
    { name: 'Admin', href: '/admin', icon: ShieldCheckIcon },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const currentNavigation = user ? dashboardNavigation : landingNavigation;
  const isDashboard = !!user;

  const isActive = (href) => {
    if (isDashboard) return location.pathname === href;
    return false; // Active state logic for anchors can be complex, skipping for now as hover is enough
  };

  const handleLogoClick = (e) => {
    if (!user && location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav className="bg-white dark:bg-dark-800 shadow-sm border-b border-gray-200 dark:border-dark-700 fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and main navigation */}
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to={user ? "/dashboard" : "/"} onClick={handleLogoClick} className="flex items-center space-x-3">
                <img
                  src="/TGlogo.png"
                  alt="TypoGuard Logo"
                  className="w-8 h-8 object-contain"
                />
                <span className="text-xl font-bold text-mint-900 dark:text-white">TypoGuard</span>
              </Link>
            </div>

            {/* Desktop navigation */}
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {currentNavigation.map((item) => {
                const Icon = item.icon;
                const isDivider = !isDashboard && item.name === 'FAQ';

                const linkContent = (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${!isDashboard
                      ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      : isActive(item.href)
                        ? 'border-mint-500 text-mint-900 dark:text-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    {!isDashboard ? null : <Icon className="w-4 h-4 mr-2" />}
                    {item.name}
                  </Link>
                );

                if (isDivider) {
                  return (
                    <React.Fragment key={item.name + "-fragment"}>
                      {linkContent}
                      <span className="h-6 border-l border-gray-300 dark:border-gray-600 mx-3 self-center hidden sm:block" aria-hidden="true" />
                    </React.Fragment>
                  );
                }

                return linkContent;
              })}
              {user?.is_admin && adminNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${isActive(item.href)
                      ? 'border-mint-500 text-mint-900 dark:text-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right side */}
          <div className="hidden sm:ml-12 sm:items-center sm:space-x-6 sm:flex">
            {user ? (
              <div className="relative flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <TGChibi size="sm" animation="none" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{user.email}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-mint-500"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition"
                >
                  Log in
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="sm:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-mint-500"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {currentNavigation.map((item) => {
              const Icon = item.icon;
              const commonClasses = "flex items-center pl-3 pr-4 py-2 border-l-4 text-base font-medium border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-dark-700";
              const activeClasses = "bg-mint-50 border-mint-500 text-mint-700 dark:bg-mint-900/20 dark:text-mint-300";
              const isDivider = !isDashboard && item.name === 'FAQ';

              const linkContent = (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${commonClasses} ${isDashboard && isActive(item.href) ? activeClasses : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );

              if (isDivider) {
                return (
                  <React.Fragment key={item.name + "-fragment"}>
                    {linkContent}
                    <div className="border-t border-gray-200 dark:border-gray-700 my-2 mx-4" />
                  </React.Fragment>
                );
              }

              return linkContent;
            })}
            {user?.is_admin && adminNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center pl-3 pr-4 py-2 border-l-4 text-base font-medium ${isActive(item.href)
                    ? 'bg-mint-50 border-mint-500 text-mint-700 dark:bg-mint-900/20 dark:text-mint-300'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-dark-700'
                    }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </div>
          <div className="pt-4 pb-3 border-t border-gray-200 dark:border-dark-700">
            {user ? (
              <>
                <div className="flex items-center px-4">
                  <div className="flex-shrink-0">
                    <TGChibi size="sm" animation="none" />
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-800 dark:text-gray-200">{user.email}</div>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <button
                    onClick={handleLogout}
                    className="flex items-center px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-dark-700 w-full text-left"
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                    Sign out
                  </button>
                </div>
              </>
            ) : (
              <div className="px-4 py-3 space-y-3">
                <Link
                  to="/login"
                  className="block text-center bg-primary-600 text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Log in
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
